import { useState, useEffect, useMemo } from "react"
import { Flex, Heading, Text, Button, Table, Thead, Tbody, Tr, Th, Td, Input, Select, Badge, useColorModeValue, IconButton, Box, useToast, InputGroup, InputLeftElement, Menu, MenuButton, MenuList, MenuItem, Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton, VStack, SimpleGrid, Tooltip, HStack, Portal, Popover, PopoverTrigger, PopoverContent, useDisclosure } from "@chakra-ui/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faTrash, faEnvelope, faBan, faCheckCircle, faSearch, faFilter, faChevronDown, faUsers, faUserCheck, faEnvelopeOpenText, faSitemap } from "@fortawesome/free-solid-svg-icons"
import { apiGetRequest, apiPostRequest, apiDeleteRequest, apiPutRequest } from "@/functions/requests"
import { useAuthenticator } from "@aws-amplify/ui-react"
import CustomDropdown from "../components/customDropdown"
import Loading from "@/components/loading"

interface User {
    email: string
    role: string
    org: string
    status: string
}

export default function UserManagement() {
    const [users, setUsers] = useState<User[]>([])
    const [loading, setLoading] = useState(true)
    const [inviteEmail, setInviteEmail] = useState("")
    const [inviteRole, setInviteRole] = useState("Instructor")
    const [inviteOrg, setInviteOrg] = useState("Global")
    const [isInviting, setIsInviting] = useState(false)
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
    const [userToDelete, setUserToDelete] = useState<string | null>(null)
    
    // Search and Filter states
    const [searchQuery, setSearchQuery] = useState("")
    const [filterOrg, setFilterOrg] = useState("All Organizations")
    const [filterRole, setFilterRole] = useState("All Roles")
    const [filterStatus, setFilterStatus] = useState("All Statuses")

    // Custom organizations state
    const [customOrgs, setCustomOrgs] = useState<string[]>([])
    const [orgSearchQuery, setOrgSearchQuery] = useState("")
    const orgPopoverDisclosure = useDisclosure()

    const toast = useToast()

    const bg = useColorModeValue("white", "gray.800")
    const borderColor = useColorModeValue("gray.200", "gray.700")

    const displayOrgName = (orgName: string): string => {
        if (!orgName || orgName === "Global" || orgName === "None") return "XNav";
        return orgName;
    };

    const uniqueOrgs = useMemo(() => {
        const orgsFromUsers = users.map(u => u.org === "Global" ? "XNav" : u.org).filter(Boolean)
        const mappedCustomOrgs = customOrgs.map(o => o === "Global" ? "XNav" : o)
        return Array.from(new Set([...orgsFromUsers, ...mappedCustomOrgs]))
    }, [users, customOrgs])

    const uniqueRoles = useMemo(() => Array.from(new Set(users.map(u => u.role).filter(Boolean))), [users])

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase())
            const userOrgMapped = user.org === "Global" ? "XNav" : user.org
            const matchesOrg = filterOrg === "All Organizations" || userOrgMapped === filterOrg
            const matchesRole = filterRole === "All Roles" || user.role === filterRole
            const matchesStatus = filterStatus === "All Statuses" || user.status.toLowerCase() === filterStatus.toLowerCase()
            return matchesSearch && matchesOrg && matchesRole && matchesStatus
        })
    }, [users, searchQuery, filterOrg, filterRole, filterStatus])

    const fetchCustomOrgs = async () => {
        try {
            const data = await apiGetRequest<undefined, { orgs?: string[] }>(`/users/custom_organizations`)
            if (data && data.orgs) {
                setCustomOrgs(data.orgs)
            }
        } catch (error) {
            console.error("Error fetching custom orgs", error)
        }
    }

    useEffect(() => {
        fetchUsers()
        fetchCustomOrgs()
    }, [])

    const fetchUsers = async () => {
        try {
            const data = await apiGetRequest<undefined, User[]>('/users')
            setUsers(data || [])
        } catch (error) {
            console.error("Error fetching users", error)
        } finally {
            setLoading(false)
        }
    }

    const handleInvite = async () => {
        if (!inviteEmail) {
            toast({ title: "Error", description: "Email is required", status: "error", duration: 3000 })
            return
        }
        setIsInviting(true)
        try {
            const cleanEmail = inviteEmail.trim().toLowerCase()
            await apiPostRequest('/users', { email: cleanEmail, role: inviteRole, org: inviteOrg })
            toast({ title: "Success", description: "User invited successfully", status: "success", duration: 3000 })
            setInviteEmail("")
            setIsInviteModalOpen(false)
            fetchUsers()
        } catch (error) {
            toast({ title: "Error", description: "Failed to invite user", status: "error", duration: 3000 })
        } finally {
            setIsInviting(false)
        }
    }

    const handleDeleteUser = async (email: string) => {
        try {
            await apiDeleteRequest(`/users/${encodeURIComponent(email)}`)
            toast({ title: "Success", description: "User deleted successfully", status: "success", duration: 3000 })
            fetchUsers()
        } catch (error) {
            toast({ title: "Error", description: "Failed to delete user", status: "error", duration: 3000 })
        }
    }

    const handleResend = async (email: string, role: string, org: string) => {
        try {
            toast({ title: "Sending...", description: "Resending invitation", status: "info", duration: 2000 })
            await apiPostRequest('/users', { email, role, org })
            toast({ title: "Success", description: "Invitation resent successfully", status: "success", duration: 3000 })
            fetchUsers()
        } catch (error) {
            toast({ title: "Error", description: "Failed to resend invitation", status: "error", duration: 3000 })
        }
    }

    const handleUpdateRole = async (email: string, newRole: string) => {
        try {
            await apiPutRequest(`/users/${encodeURIComponent(email)}`, { role: newRole })
            toast({ title: "Success", description: "User role updated successfully", status: "success", duration: 3000 })
            fetchUsers()
        } catch (error) {
            toast({ title: "Error", description: "Failed to update user role", status: "error", duration: 3000 })
        }
    }

    const handleUpdateStatus = async (email: string, newStatus: string) => {
        try {
            await apiPutRequest(`/users/${encodeURIComponent(email)}`, { status: newStatus })
            toast({ title: "Success", description: `User suspended/activated successfully`, status: "success", duration: 3000 })
            fetchUsers()
        } catch (error) {
            toast({ title: "Error", description: "Failed to update user status", status: "error", duration: 3000 })
        }
    }

    const isGlobalOrg = (orgName: string) => !orgName || orgName === "Global" || orgName === "None";

    const handleUpdateOrg = async (email: string, newOrg: string) => {
        try {
            const targetUser = users.find(u => u.email === email)
            const isTargetGlobal = isGlobalOrg(newOrg)
            
            if (!isTargetGlobal && targetUser && (targetUser.role === "Admin" || targetUser.role === "Super Admin")) {
                await apiPutRequest(`/users/${encodeURIComponent(email)}`, { org: newOrg, role: "Org Admin" })
                toast({ title: "Success", description: "User organization updated and role demoted to Org Admin", status: "success", duration: 4000 })
            } else {
                await apiPutRequest(`/users/${encodeURIComponent(email)}`, { org: newOrg })
                toast({ title: "Success", description: "User organization updated successfully", status: "success", duration: 3000 })
            }
            fetchUsers()
        } catch (error) {
            toast({ title: "Error", description: "Failed to update user organization", status: "error", duration: 3000 })
        }
    }

    const handleInviteOrgChange = (val: string) => {
        const dbOrg = val === "XNav" ? "Global" : val;
        setInviteOrg(dbOrg)
        if (!isGlobalOrg(dbOrg)) {
            if (inviteRole === "Admin" || inviteRole === "Super Admin") {
                setInviteRole("Instructor")
            }
        }
    }

    // Determine current user's role to restrict Super Admin / Org Admin features
    const { user } = useAuthenticator()
    const currentUserEmail = user?.signInDetails?.loginId || ''
    const currentUserItem = users.find(u => u.email === currentUserEmail)
    const currentUserRole = currentUserItem?.role || 'Instructor'
    const currentUserOrg = currentUserItem?.org || 'Global'
    const isSuperAdmin = currentUserRole === 'Super Admin'
    const isOrgAdmin = currentUserRole === 'Org Admin'

    const stats = useMemo(() => {
        const total = users.length
        const active = users.filter(u => u.status.toLowerCase() === 'active').length
        const invited = users.filter(u => u.status.toLowerCase() === 'invited').length
        const orgs = new Set(users.map(u => u.org).filter(Boolean)).size
        return { total, active, invited, orgs }
    }, [users])

    const getAvatarData = (email: string) => {
        const parts = email.split('@')[0]
        const initials = parts.substring(0, 2).toUpperCase()
        const gradients = [
            "linear(to-br, blue.400, purple.500)",
            "linear(to-br, purple.400, pink.500)",
            "linear(to-br, teal.400, blue.500)",
            "linear(to-br, orange.400, red.500)",
            "linear(to-br, green.400, teal.500)",
            "linear(to-br, pink.400, orange.500)",
        ]
        let sum = 0
        for (let i = 0; i < email.length; i++) {
            sum += email.charCodeAt(i)
        }
        const gradient = gradients[sum % gradients.length]
        return { initials, gradient }
    }

    const getRoleColorScheme = (role: string) => {
        switch (role) {
            case 'Super Admin': return 'red'
            case 'Admin': return 'purple'
            case 'Org Admin': return 'orange'
            default: return 'blue'
        }
    }

    const StatCard = ({ title, value, icon, colorScheme }: { title: string, value: number, icon: any, colorScheme: string }) => {
        const bgCard = useColorModeValue("white", "#111827")
        const borderColor = useColorModeValue("gray.200", "#374151")
        const mutedTextColor = useColorModeValue("gray.500", "gray.400")
        
        return (
            <Box 
                bg={bgCard} 
                p={4} 
                borderRadius="xl" 
                border="1px solid" 
                borderColor={borderColor}
            >
                <Flex align="center" justify="space-between">
                    <VStack align="start" spacing={0.5}>
                        <Text fontSize="xs" fontWeight="600" color={mutedTextColor}>
                            {title}
                        </Text>
                        <Heading size="md" fontWeight="800" color={useColorModeValue("gray.900", "white")}>
                            {value}
                        </Heading>
                    </VStack>
                    <Flex 
                        w={10} 
                        h={10} 
                        align="center" 
                        justify="center" 
                        borderRadius="lg" 
                        bg="rgba(16, 185, 129, 0.1)" 
                        color="#10b981"
                    >
                        <FontAwesomeIcon icon={icon} />
                    </Flex>
                </Flex>
            </Box>
        )
    }

    useEffect(() => {
        if (isOrgAdmin && currentUserOrg) {
            setInviteOrg(currentUserOrg)
        }
    }, [isOrgAdmin, currentUserOrg])

    if (loading) {
        return <Loading message="Loading User Directory..." />
    }

    return (
        <Flex flexDir="column" gap={6} h="100%" w="100%" maxW="1500px" mx="auto" p={{ base: 4, md: 6 }}>
            <Flex justifyContent="space-between" alignItems="center" wrap="wrap" gap={3}>
                <Box>
                    <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color={useColorModeValue("gray.900", "white")}>
                        User Management
                    </Heading>
                    <Text color={useColorModeValue("gray.500", "gray.400")} fontSize="sm" mt={0.5}>
                        Manage instructors, administrators, and organization permissions across the platform.
                    </Text>
                </Box>
                <Button
                    colorScheme="emerald"
                    bg="linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)"
                    color="white"
                    _hover={{ bg: "#059669" }}
                    leftIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => {
                        setInviteEmail("")
                        setInviteRole("Instructor")
                        setInviteOrg(isOrgAdmin ? currentUserOrg : "Global")
                        setIsInviteModalOpen(true)
                    }}
                    borderRadius="lg"
                    size="sm"
                >
                    Invite New User
                </Button>
            </Flex>

            {/* Stats Overview */}
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
                <StatCard 
                    title="Total Users" 
                    value={stats.total} 
                    icon={faUsers} 
                    colorScheme="emerald"
                />
                <StatCard 
                    title="Active Users" 
                    value={stats.active} 
                    icon={faUserCheck} 
                    colorScheme="emerald"
                />
                <StatCard 
                    title="Pending Invites" 
                    value={stats.invited} 
                    icon={faEnvelopeOpenText} 
                    colorScheme="emerald"
                />
                <StatCard 
                    title="Organizations" 
                    value={stats.orgs} 
                    icon={faSitemap} 
                    colorScheme="emerald"
                />
            </SimpleGrid>



            {/* Search and Filter Panel */}
            <Flex 
                w="100%" 
                bg={bg} 
                p={4} 
                borderRadius="2xl" 
                boxShadow={useColorModeValue("0 4px 20px rgba(0, 0, 0, 0.02)", "0 4px 20px rgba(0, 0, 0, 0.2)")}
                border="1px solid" 
                borderColor={borderColor}
                flexDir={{ base: "column", md: "row" }}
                alignItems="center"
                justifyContent="space-between"
                gap={4}
            >
                <InputGroup size="md" maxW={{ base: "100%", md: "350px" }}>
                    <InputLeftElement pointerEvents="none" h="full">
                        <FontAwesomeIcon icon={faSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                        placeholder="Search emails..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        bg={useColorModeValue("gray.50", "gray.900/50")}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="xl"
                        _focus={{ 
                            borderColor: "green.500", 
                            boxShadow: "0 0 0 1px #0ea5e9",
                            bg: useColorModeValue("white", "gray.900")
                        }}
                        _hover={{ borderColor: "green.300" }}
                        fontSize="md"
                        pl={10}
                    />
                </InputGroup>

                <Flex gap={2} w={{ base: "100%", md: "auto" }} flexWrap="wrap" alignItems="center" justify={{ base: "flex-start", md: "flex-end" }}>
                    <Popover
                        isOpen={orgPopoverDisclosure.isOpen}
                        onOpen={orgPopoverDisclosure.onOpen}
                        onClose={orgPopoverDisclosure.onClose}
                        placement="bottom-start"
                        matchWidth
                    >
                        <PopoverTrigger>
                            <Button 
                                size="sm"
                                variant="outline" 
                                borderRadius="lg" 
                                borderColor={borderColor}
                                leftIcon={<FontAwesomeIcon icon={faFilter} color="#0ea5e9" style={{ fontSize: '12px' }} />}
                                rightIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                                fontWeight="600"
                                fontSize="xs"
                                color={useColorModeValue("gray.700", "gray.200")}
                                _hover={{ bg: useColorModeValue("rgba(16, 185, 129, 0.08)", "rgba(16, 185, 129, 0.12)") }}
                                _focus={{ outline: "none", boxShadow: "none" }}
                                _focusVisible={{ outline: "none", boxShadow: "none" }}
                            >
                                {filterOrg === "All Organizations" ? "All Organizations" : displayOrgName(filterOrg)}
                            </Button>
                        </PopoverTrigger>
                        <Portal>
                            <PopoverContent
                                bg={useColorModeValue("white", "#111827")}
                                borderColor={borderColor}
                                shadow="xl"
                                borderRadius="xl"
                                overflow="hidden"
                                _focus={{ outline: 'none' }}
                                _focusVisible={{ outline: "none" }}
                                w="240px"
                                zIndex={1500}
                                p={1.5}
                            >
                                <Box p={1.5} borderBottom="1px solid" borderColor={useColorModeValue("gray.100", "#1f2937")} mb={1}>
                                    <InputGroup size="xs">
                                        <InputLeftElement pointerEvents="none">
                                            <FontAwesomeIcon icon={faSearch} color="#9ca3af" />
                                        </InputLeftElement>
                                        <Input
                                            placeholder="Search orgs..."
                                            value={orgSearchQuery}
                                            onChange={(e) => setOrgSearchQuery(e.target.value)}
                                            bg={useColorModeValue("gray.50", "#1f2937")}
                                            border="1px solid"
                                            borderColor={borderColor}
                                            borderRadius="md"
                                            fontSize="xs"
                                            _focus={{ 
                                                borderColor: "#0ea5e9", 
                                                boxShadow: "none",
                                            }}
                                            _focusVisible={{ outline: "none" }}
                                        />
                                    </InputGroup>
                                </Box>
                                <Box maxH="240px" overflowY="auto" py={0.5}>
                                    {("All Organizations").toLowerCase().includes(orgSearchQuery.toLowerCase()) && (
                                        <Box
                                            px={3}
                                            py={2}
                                            borderRadius="md"
                                            cursor="pointer"
                                            bg={filterOrg === "All Organizations" ? useColorModeValue("rgba(16, 185, 129, 0.1)", "rgba(16, 185, 129, 0.15)") : "transparent"}
                                            color={filterOrg === "All Organizations" ? "#10b981" : useColorModeValue("gray.800", "gray.200")}
                                            _hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
                                            onClick={() => {
                                                setFilterOrg("All Organizations");
                                                orgPopoverDisclosure.onClose();
                                                setOrgSearchQuery("");
                                            }}
                                            fontWeight={filterOrg === "All Organizations" ? "700" : "500"}
                                            fontSize="xs"
                                            transition="all 0.15s ease"
                                        >
                                            All Organizations
                                        </Box>
                                    )}

                                    {uniqueOrgs.filter(org => org.toLowerCase().includes(orgSearchQuery.toLowerCase())).map(org => (
                                        <Box
                                            key={org}
                                            px={3}
                                            py={2}
                                            borderRadius="md"
                                            cursor="pointer"
                                            bg={filterOrg === org ? useColorModeValue("rgba(16, 185, 129, 0.1)", "rgba(16, 185, 129, 0.15)") : "transparent"}
                                            color={filterOrg === org ? "#10b981" : useColorModeValue("gray.800", "gray.200")}
                                            _hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
                                            onClick={() => {
                                                setFilterOrg(org);
                                                orgPopoverDisclosure.onClose();
                                                setOrgSearchQuery("");
                                            }}
                                            fontWeight={filterOrg === org ? "700" : "500"}
                                            fontSize="xs"
                                            transition="all 0.15s ease"
                                        >
                                            {displayOrgName(org)}
                                        </Box>
                                    ))}

                                    {isSuperAdmin && orgSearchQuery.trim() !== "" && !uniqueOrgs.find(org => org.toLowerCase() === orgSearchQuery.trim().toLowerCase()) && orgSearchQuery.trim().toLowerCase() !== "all organizations" && (
                                        <Box
                                            px={3}
                                            py={2}
                                            borderRadius="md"
                                            cursor="pointer"
                                            color="#10b981"
                                            _hover={{ bg: useColorModeValue("rgba(16, 185, 129, 0.08)", "rgba(16, 185, 129, 0.12)") }}
                                            onClick={async () => {
                                                const trimmed = orgSearchQuery.trim();
                                                const updatedOrgs = [...customOrgs, trimmed];
                                                setCustomOrgs(updatedOrgs);
                                                setFilterOrg(trimmed);
                                                toast({
                                                    title: "Success",
                                                    description: `Organization "${trimmed}" created successfully`,
                                                    status: "success",
                                                    duration: 3000
                                                });
                                                orgPopoverDisclosure.onClose();
                                                setOrgSearchQuery("");

                                                try {
                                                    await apiPutRequest(`/users/custom_organizations`, { orgs: updatedOrgs })
                                                } catch (e) {
                                                    console.error("Failed to persist custom organization", e);
                                                }
                                            }}
                                            fontWeight="bold"
                                            fontSize="xs"
                                            transition="all 0.15s ease"
                                            borderTop="1px dashed"
                                            borderColor={borderColor}
                                            mt={1}
                                        >
                                            <Flex align="center" gap={2}>
                                                <FontAwesomeIcon icon={faPlus} size="xs" />
                                                <Text isTruncated>Create "{orgSearchQuery}"</Text>
                                            </Flex>
                                        </Box>
                                    )}

                                    {uniqueOrgs.filter(org => org.toLowerCase().includes(orgSearchQuery.toLowerCase())).length === 0 && 
                                     !(isSuperAdmin && orgSearchQuery.trim() !== "" && !uniqueOrgs.find(org => org.toLowerCase() === orgSearchQuery.trim().toLowerCase())) && (
                                        <Box px={3} py={2} color="gray.500" fontSize="xs" fontStyle="italic">
                                            No organizations found
                                        </Box>
                                    )}
                                </Box>
                            </PopoverContent>
                        </Portal>
                    </Popover>

                    <Menu isLazy>
                        <MenuButton 
                            as={Button} 
                            size="sm"
                            variant="outline" 
                            borderRadius="lg" 
                            borderColor={borderColor}
                            leftIcon={<FontAwesomeIcon icon={faFilter} color="#0ea5e9" style={{ fontSize: '12px' }} />}
                            rightIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                            fontWeight="600"
                            fontSize="xs"
                            color={useColorModeValue("gray.700", "gray.200")}
                            _hover={{ bg: useColorModeValue("rgba(16, 185, 129, 0.08)", "rgba(16, 185, 129, 0.12)") }}
                            _focus={{ outline: "none", boxShadow: "none" }}
                            _focusVisible={{ outline: "none", boxShadow: "none" }}
                        >
                            {filterRole === "All Roles" ? "All Roles" : filterRole}
                        </MenuButton>
                        <Portal>
                            <MenuList shadow="xl" borderRadius="xl" border="1px solid" borderColor={borderColor} bg={useColorModeValue("white", "#111827")} p={1.5} py={1} zIndex={1500}>
                                <MenuItem 
                                    py={2} 
                                    px={3}
                                    borderRadius="md" 
                                    fontSize="xs"
                                    fontWeight={filterRole === "All Roles" ? "700" : "500"} 
                                    bg={filterRole === "All Roles" ? useColorModeValue("rgba(16, 185, 129, 0.1)", "rgba(16, 185, 129, 0.15)") : "transparent"}
                                    color={filterRole === "All Roles" ? "#10b981" : useColorModeValue("gray.800", "gray.200")}
                                    _hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
                                    _focus={{ outline: "none" }}
                                    _focusVisible={{ outline: "none", boxShadow: "none" }}
                                    onClick={() => setFilterRole("All Roles")}
                                >
                                    All Roles
                                </MenuItem>
                                {uniqueRoles.map(role => (
                                    <MenuItem 
                                        key={role} 
                                        py={2} 
                                        px={3}
                                        borderRadius="md" 
                                        fontSize="xs"
                                        fontWeight={filterRole === role ? "700" : "500"} 
                                        bg={filterRole === role ? useColorModeValue("rgba(16, 185, 129, 0.1)", "rgba(16, 185, 129, 0.15)") : "transparent"}
                                        color={filterRole === role ? "#10b981" : useColorModeValue("gray.800", "gray.200")}
                                        _hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
                                        _focus={{ outline: "none" }}
                                        _focusVisible={{ outline: "none", boxShadow: "none" }}
                                        onClick={() => setFilterRole(role)}
                                    >
                                        {role}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Portal>
                    </Menu>

                    <Menu isLazy>
                        <MenuButton 
                            as={Button} 
                            size="sm"
                            variant="outline" 
                            borderRadius="lg" 
                            borderColor={borderColor}
                            leftIcon={<FontAwesomeIcon icon={faFilter} color="#0ea5e9" style={{ fontSize: '12px' }} />}
                            rightIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                            fontWeight="600"
                            fontSize="xs"
                            color={useColorModeValue("gray.700", "gray.200")}
                            _hover={{ bg: useColorModeValue("rgba(16, 185, 129, 0.08)", "rgba(16, 185, 129, 0.12)") }}
                            _focus={{ outline: "none", boxShadow: "none" }}
                            _focusVisible={{ outline: "none", boxShadow: "none" }}
                        >
                            {filterStatus === "All Statuses" ? "All Statuses" : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
                        </MenuButton>
                        <Portal>
                            <MenuList shadow="xl" borderRadius="xl" border="1px solid" borderColor={borderColor} bg={useColorModeValue("white", "#111827")} p={1.5} py={1} zIndex={1500}>
                                <MenuItem 
                                    py={2} 
                                    px={3}
                                    borderRadius="md" 
                                    fontSize="xs"
                                    fontWeight={filterStatus === "All Statuses" ? "700" : "500"} 
                                    bg={filterStatus === "All Statuses" ? useColorModeValue("rgba(16, 185, 129, 0.1)", "rgba(16, 185, 129, 0.15)") : "transparent"}
                                    color={filterStatus === "All Statuses" ? "#10b981" : useColorModeValue("gray.800", "gray.200")}
                                    _hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
                                    _focus={{ outline: "none" }}
                                    _focusVisible={{ outline: "none", boxShadow: "none" }}
                                    onClick={() => setFilterStatus("All Statuses")}
                                >
                                    All Statuses
                                </MenuItem>
                                <MenuItem 
                                    py={2} 
                                    px={3}
                                    borderRadius="md" 
                                    fontSize="xs"
                                    fontWeight={filterStatus === "active" ? "700" : "500"} 
                                    bg={filterStatus === "active" ? useColorModeValue("rgba(16, 185, 129, 0.1)", "rgba(16, 185, 129, 0.15)") : "transparent"}
                                    color={filterStatus === "active" ? "#10b981" : useColorModeValue("gray.800", "gray.200")}
                                    _hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
                                    _focus={{ outline: "none" }}
                                    _focusVisible={{ outline: "none", boxShadow: "none" }}
                                    onClick={() => setFilterStatus("active")}
                                >
                                    Active
                                </MenuItem>
                                <MenuItem 
                                    py={2} 
                                    px={3}
                                    borderRadius="md" 
                                    fontSize="xs"
                                    fontWeight={filterStatus === "inactive" ? "700" : "500"} 
                                    bg={filterStatus === "inactive" ? useColorModeValue("rgba(16, 185, 129, 0.1)", "rgba(16, 185, 129, 0.15)") : "transparent"}
                                    color={filterStatus === "inactive" ? "#10b981" : useColorModeValue("gray.800", "gray.200")}
                                    _hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
                                    _focus={{ outline: "none" }}
                                    _focusVisible={{ outline: "none", boxShadow: "none" }}
                                    onClick={() => setFilterStatus("inactive")}
                                >
                                    Inactive
                                </MenuItem>
                                <MenuItem 
                                    py={2} 
                                    px={3}
                                    borderRadius="md" 
                                    fontSize="xs"
                                    fontWeight={filterStatus === "invited" ? "700" : "500"} 
                                    bg={filterStatus === "invited" ? useColorModeValue("rgba(16, 185, 129, 0.1)", "rgba(16, 185, 129, 0.15)") : "transparent"}
                                    color={filterStatus === "invited" ? "#10b981" : useColorModeValue("gray.800", "gray.200")}
                                    _hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
                                    _focus={{ outline: "none" }}
                                    _focusVisible={{ outline: "none", boxShadow: "none" }}
                                    onClick={() => setFilterStatus("invited")}
                                >
                                    Invited
                                </MenuItem>
                            </MenuList>
                        </Portal>
                    </Menu>

                    {(filterOrg !== "All Organizations" || filterRole !== "All Roles" || filterStatus !== "All Statuses" || searchQuery !== "") && (
                        <Button 
                            size="sm" 
                            variant="ghost" 
                            colorScheme="green" 
                            onClick={() => {
                                setFilterOrg("All Organizations")
                                setFilterRole("All Roles")
                                setFilterStatus("All Statuses")
                                setSearchQuery("")
                            }}
                        >
                            Clear
                        </Button>
                    )}
                </Flex>
            </Flex>

            {/* User List Table */}
            <Box 
                bg={bg} 
                borderRadius="2xl" 
                shadow={useColorModeValue("0 4px 20px rgba(0, 0, 0, 0.02)", "0 4px 20px rgba(0, 0, 0, 0.2)")}
                border="1px solid" 
                borderColor={borderColor} 
                overflow="hidden"
            >
                <Table variant="simple">
                    <Thead bg={useColorModeValue("gray.50", "gray.900/40")}>
                        <Tr>
                            <Th fontSize="xs" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider" py={4}>Email</Th>
                            <Th fontSize="xs" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider" py={4}>Role</Th>
                            <Th fontSize="xs" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider" py={4}>Organization</Th>
                            <Th fontSize="xs" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider" py={4}>Status</Th>
                            <Th fontSize="xs" fontWeight="extrabold" textTransform="uppercase" letterSpacing="wider" py={4} isNumeric>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {loading ? (
                            <Tr><Td colSpan={5} textAlign="center" py={8} color="gray.500">Loading users...</Td></Tr>
                        ) : filteredUsers.length === 0 ? (
                            <Tr><Td colSpan={5} textAlign="center" py={8} color="gray.500">No users match your criteria.</Td></Tr>
                        ) : (
                            filteredUsers.map((user, idx) => {
                                const { initials, gradient } = getAvatarData(user.email)
                                return (
                                    <Tr 
                                        key={idx}
                                        _hover={{ 
                                            bg: useColorModeValue("purple.50/10", "whiteAlpha.50"), 
                                            transform: "translateY(-0.5px)"
                                        }}
                                        transition="all 0.2s"
                                    >
                                        <Td py={4}>
                                            <HStack spacing={3}>
                                                <Flex 
                                                    w={9} 
                                                    h={9} 
                                                    borderRadius="full" 
                                                    bgGradient={gradient} 
                                                    color="white" 
                                                    align="center" 
                                                    justify="center" 
                                                    fontWeight="bold" 
                                                    fontSize="xs"
                                                    shadow="sm"
                                                >
                                                    {initials}
                                                </Flex>
                                                <VStack align="start" spacing={0}>
                                                    <Text fontWeight="semibold" color={useColorModeValue("gray.800", "gray.100")}>
                                                        {user.email}
                                                    </Text>
                                                    {user.email === currentUserEmail && (
                                                        <Text fontSize="10px" color="purple.500" fontWeight="bold">You</Text>
                                                    )}
                                                </VStack>
                                            </HStack>
                                        </Td>
                                        <Td py={4}>
                                            {user.email === currentUserEmail ? (
                                                <Badge 
                                                    px={3} 
                                                    py={1.5} 
                                                    borderRadius="lg" 
                                                    colorScheme={getRoleColorScheme(user.role)}
                                                    fontSize="xs"
                                                    fontWeight="bold"
                                                >
                                                    {user.role}
                                                </Badge>
                                            ) : (
                                                <Menu isLazy>
                                                    <MenuButton 
                                                        as={Button} 
                                                        size="xs" 
                                                        variant="ghost" 
                                                        px={3} 
                                                        py={1.5}
                                                        h="auto"
                                                        borderRadius="lg" 
                                                        colorScheme={getRoleColorScheme(user.role)}
                                                        fontWeight="bold"
                                                        _hover={{ bg: useColorModeValue(`${getRoleColorScheme(user.role)}.50`, `${getRoleColorScheme(user.role)}.900/30`) }}
                                                        rightIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                                                    >
                                                        {user.role}
                                                    </MenuButton>
                                                    <Portal>
                                                        <MenuList borderRadius="xl" shadow="2xl" border="none" py={1}>
                                                            <MenuItem py={2} fontWeight="bold" color="blue.500" onClick={() => handleUpdateRole(user.email, "Instructor")}>Instructor</MenuItem>
                                                            {isSuperAdmin && isGlobalOrg(user.org) && <MenuItem py={2} fontWeight="bold" color="purple.500" onClick={() => handleUpdateRole(user.email, "Admin")}>Admin</MenuItem>}
                                                            {isSuperAdmin && isGlobalOrg(user.org) && <MenuItem py={2} fontWeight="bold" color="red.500" onClick={() => handleUpdateRole(user.email, "Super Admin")}>Super Admin</MenuItem>}
                                                            {(isSuperAdmin || isOrgAdmin) && <MenuItem py={2} fontWeight="bold" color="orange.500" onClick={() => handleUpdateRole(user.email, "Org Admin")}>Org Admin</MenuItem>}
                                                        </MenuList>
                                                    </Portal>
                                                </Menu>
                                            )}
                                        </Td>
                                        <Td py={4}>
                                            {isSuperAdmin && user.email !== currentUserEmail ? (
                                                <Menu isLazy>
                                                    <MenuButton 
                                                        as={Button} 
                                                        size="xs" 
                                                        variant="outline" 
                                                        px={3} 
                                                        py={1.5}
                                                        h="auto"
                                                        borderRadius="lg" 
                                                        borderColor={borderColor}
                                                        fontWeight="semibold"
                                                        _hover={{ bg: useColorModeValue("gray.50", "gray.750") }}
                                                        rightIcon={<FontAwesomeIcon icon={faChevronDown} size="xs" />}
                                                    >
                                                        {displayOrgName(user.org)}
                                                    </MenuButton>
                                                    <Portal>
                                                        <MenuList borderRadius="xl" shadow="2xl" border="none" maxH="300px" overflowY="auto" py={1}>
                                                            {uniqueOrgs.map(org => (
                                                                <MenuItem key={org} py={2} onClick={() => handleUpdateOrg(user.email, org === "XNav" ? "Global" : org)}>
                                                                    {displayOrgName(org)}
                                                                </MenuItem>
                                                            ))}
                                                        </MenuList>
                                                    </Portal>
                                                </Menu>
                                            ) : (
                                                <Badge px={3} py={1.5} borderRadius="lg" colorScheme="gray" fontSize="xs" fontWeight="semibold">
                                                    {displayOrgName(user.org)}
                                                </Badge>
                                            )}
                                        </Td>
                                        <Td py={4}>
                                            <Badge 
                                                px={3} 
                                                py={1.5} 
                                                borderRadius="full" 
                                                variant="subtle" 
                                                colorScheme={
                                                    user.status.toLowerCase() === 'active' ? 'green' : 
                                                    user.status.toLowerCase() === 'inactive' ? 'red' : 'yellow'
                                                }
                                                display="inline-flex"
                                                alignItems="center"
                                                gap={2}
                                            >
                                                <Box 
                                                    w="6px" 
                                                    h="6px" 
                                                    borderRadius="full" 
                                                    bg={
                                                        user.status.toLowerCase() === 'active' ? 'green.500' : 
                                                        user.status.toLowerCase() === 'inactive' ? 'red.500' : 'yellow.500'
                                                    }
                                                    animation={user.status.toLowerCase() === 'active' ? "pulse 2s infinite" : undefined}
                                                    sx={{
                                                        "@keyframes pulse": {
                                                            "0%": { boxShadow: "0 0 0 0 rgba(72, 187, 120, 0.7)" },
                                                            "70%": { boxShadow: "0 0 0 4px rgba(72, 187, 120, 0)" },
                                                            "100%": { boxShadow: "0 0 0 0 rgba(72, 187, 120, 0)" }
                                                        }
                                                    }}
                                                />
                                                <Text fontSize="10px" fontWeight="extrabold">
                                                    {user.status}
                                                </Text>
                                            </Badge>
                                        </Td>
                                        <Td py={4} isNumeric>
                                            <HStack spacing={2} justify="flex-end">
                                                {user.status.toLowerCase() !== 'active' && (
                                                    <Tooltip label="Resend Invitation" fontSize="xs" borderRadius="md">
                                                        <IconButton
                                                            aria-label="Resend invitation"
                                                            icon={<FontAwesomeIcon icon={faEnvelope} />}
                                                            size="sm"
                                                            variant="ghost"
                                                            colorScheme="purple"
                                                            onClick={() => handleResend(user.email, user.role, user.org)}
                                                        />
                                                    </Tooltip>
                                                )}
                                                
                                                {(isSuperAdmin || isOrgAdmin) && user.email !== currentUserEmail && (
                                                    <Tooltip 
                                                        label={user.status.toLowerCase() === 'inactive' ? "Activate User" : "Suspend User"} 
                                                        fontSize="xs" 
                                                        borderRadius="md"
                                                    >
                                                        <IconButton
                                                            aria-label={user.status.toLowerCase() === 'inactive' ? "Activate user" : "Suspend user"}
                                                            icon={<FontAwesomeIcon icon={user.status.toLowerCase() === 'inactive' ? faCheckCircle : faBan} />}
                                                            size="sm"
                                                            variant="ghost"
                                                            colorScheme={user.status.toLowerCase() === 'inactive' ? "green" : "orange"}
                                                            onClick={() => handleUpdateStatus(user.email, user.status.toLowerCase() === 'inactive' ? 'Active' : 'Inactive')}
                                                        />
                                                    </Tooltip>
                                                )}
                                                
                                                {user.email !== currentUserEmail && (
                                                    <Tooltip label="Delete User" fontSize="xs" borderRadius="md">
                                                        <IconButton 
                                                            aria-label="Delete user" 
                                                            icon={<FontAwesomeIcon icon={faTrash} />} 
                                                            size="sm" 
                                                            colorScheme="red" 
                                                            variant="ghost"
                                                            onClick={() => setUserToDelete(user.email)} 
                                                        />
                                                    </Tooltip>
                                                )}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                )
                            })
                        )}
                    </Tbody>
                </Table>
            </Box>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={!!userToDelete} onClose={() => setUserToDelete(null)} isCentered>
                <ModalOverlay backdropFilter="blur(8px)" />
                <ModalContent 
                    bg={useColorModeValue("white", "gray.850")}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={useColorModeValue("gray.150", "gray.750")}
                    shadow="2xl"
                >
                    <ModalCloseButton borderRadius="full" />
                    <ModalBody p={6} pt={8}>
                        <VStack spacing={4} align="center">
                            <Box 
                                bg="red.50" 
                                _dark={{ bg: "red.900/30" }} 
                                p={4} 
                                borderRadius="full" 
                                color="red.500"
                            >
                                <FontAwesomeIcon icon={faTrash} size="2x" />
                            </Box>
                            <Heading size="md" textAlign="center">Delete User Account</Heading>
                            <Text color="gray.500" textAlign="center" fontSize="sm">
                                Are you sure you want to delete <Text as="span" fontWeight="bold" color={useColorModeValue("gray.800", "white")}>{userToDelete}</Text>? This action is permanent and cannot be undone.
                            </Text>
                        </VStack>
                    </ModalBody>
                    <Flex gap={3} px={6} pb={6} justify="flex-end">
                        <Button 
                            variant="ghost" 
                            borderRadius="lg" 
                            onClick={() => setUserToDelete(null)}
                        >
                            Cancel
                        </Button>
                        <Button 
                            colorScheme="red" 
                            borderRadius="lg" 
                            onClick={() => {
                                if (userToDelete) {
                                    handleDeleteUser(userToDelete)
                                    setUserToDelete(null)
                                }
                            }}
                        >
                            Delete User
                        </Button>
                    </Flex>
                </ModalContent>
            </Modal>

            {/* Invite User Modal */}
            <Modal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} isCentered>
                <ModalOverlay backdropFilter="blur(8px)" />
                <ModalContent 
                    bg={useColorModeValue("white", "gray.850")}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor={useColorModeValue("gray.150", "gray.750")}
                    shadow="2xl"
                    maxW="450px"
                >
                    <ModalCloseButton borderRadius="full" />
                    <ModalBody p={6} pt={8}>
                        <VStack spacing={5} align="stretch">
                            <VStack spacing={1} align="center" mb={2}>
                                <Box 
                                    bg="green.50" 
                                    _dark={{ bg: "green.900/30" }} 
                                    p={3} 
                                    borderRadius="full" 
                                    color="green.500"
                                >
                                    <FontAwesomeIcon icon={faEnvelope} size="xl" />
                                </Box>
                                <Heading size="md" textAlign="center" mt={2} bgGradient="linear(to-r, #38bdf8, #0ea5e9, #10b981)" bgClip="text" fontWeight="extrabold">Invite New User</Heading>
                                <Text color="gray.500" textAlign="center" fontSize="xs">
                                    Send an email invitation to grant access to the platform.
                                </Text>
                            </VStack>

                            <VStack align="stretch" spacing={3}>
                                <Box>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1.5} textTransform="uppercase" letterSpacing="wider">
                                        Email Address
                                    </Text>
                                    <InputGroup size="md">
                                        <InputLeftElement pointerEvents="none" h="full">
                                            <FontAwesomeIcon icon={faEnvelope} color="gray.400" />
                                        </InputLeftElement>
                                        <Input 
                                            placeholder="e.g. name@organization.com" 
                                            value={inviteEmail} 
                                            onChange={(e) => setInviteEmail(e.target.value)}
                                            bg={useColorModeValue("gray.50", "gray.900/50")}
                                            border="1px solid"
                                            borderColor={borderColor}
                                            borderRadius="xl"
                                            _focus={{ 
                                                borderColor: "green.500", 
                                                boxShadow: "0 0 0 1px #0ea5e9",
                                                bg: useColorModeValue("white", "gray.900")
                                            }}
                                            fontSize="sm"
                                            pl={10}
                                        />
                                    </InputGroup>
                                </Box>

                                <Box>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1.5} textTransform="uppercase" letterSpacing="wider">
                                        System Role
                                    </Text>
                                    <Select 
                                        value={inviteRole} 
                                        onChange={(e) => setInviteRole(e.target.value)} 
                                        bg={useColorModeValue("gray.50", "gray.900/50")}
                                        border="1px solid"
                                        borderColor={borderColor}
                                        borderRadius="xl"
                                        _focus={{ 
                                            borderColor: "green.500", 
                                            boxShadow: "0 0 0 1px #0ea5e9"
                                        }}
                                        fontSize="sm"
                                    >
                                        <option value="Instructor">Instructor</option>
                                        {isSuperAdmin && isGlobalOrg(inviteOrg) && <option value="Admin">Admin</option>}
                                        {isSuperAdmin && isGlobalOrg(inviteOrg) && <option value="Super Admin">Super Admin</option>}
                                        {(isSuperAdmin || isOrgAdmin) && <option value="Org Admin">Org Admin</option>}
                                    </Select>
                                </Box>

                                <Box>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.400" mb={1.5} textTransform="uppercase" letterSpacing="wider">
                                        Organization
                                    </Text>
                                    {isSuperAdmin ? (
                                        <CustomDropdown
                                            options={uniqueOrgs}
                                            placeholder="Search or select organization..." 
                                            value={displayOrgName(inviteOrg)} 
                                            onChange={(val) => handleInviteOrgChange(val)}
                                            bg={useColorModeValue("gray.50", "gray.900/50")}
                                            border="1px solid"
                                            borderColor={borderColor}
                                            borderRadius="xl"
                                            _focus={{ 
                                                borderColor: "green.500", 
                                                boxShadow: "0 0 0 1px #0ea5e9"
                                            }}
                                            fontSize="sm"
                                        />
                                    ) : (
                                        <Flex 
                                            bg={useColorModeValue("gray.50", "gray.900/50")}
                                            border="1px solid"
                                            borderColor={borderColor}
                                            borderRadius="xl"
                                            p={3}
                                            align="center"
                                        >
                                            <Badge colorScheme="green" fontSize="xs" px={3} py={1} borderRadius="lg">
                                                {displayOrgName(currentUserOrg)}
                                            </Badge>
                                        </Flex>
                                    )}
                                </Box>
                            </VStack>

                            <Flex gap={3} justify="flex-end" mt={2}>
                                <Button 
                                    variant="ghost" 
                                    borderRadius="xl" 
                                    onClick={() => setIsInviteModalOpen(false)}
                                    size="md"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    colorScheme="green" 
                                    borderRadius="xl" 
                                    onClick={handleInvite}
                                    isLoading={isInviting}
                                    size="md"
                                    px={6}
                                >
                                    Invite User
                                </Button>
                            </Flex>
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Flex>
    )
}
